import { Calendar, ArrowRight, CheckCircle2, AlertCircle } from "lucide-react";

export const DomainWarmupSchedule = () => {
  // Cold email warmup: Conservative approach per mailbox
  // Industry best practice: Max 50-80 emails/day per mailbox after full warmup
  // Source: Instantly, Lemlist, Woodpecker 2024-2025 guides
  const coldEmailSchedule = [
    { week: 1, daily: "5-10", total: "35-70", focus: "Send to most engaged contacts only", risk: "low" },
    { week: 2, daily: "10-15", total: "70-105", focus: "Add recent engagers, monitor bounces", risk: "low" },
    { week: 3, daily: "15-25", total: "105-175", focus: "Expand to warm leads, track replies", risk: "low" },
    { week: 4, daily: "25-35", total: "175-245", focus: "Include broader prospect list", risk: "medium" },
    { week: 5, daily: "35-50", total: "245-350", focus: "Scale carefully, monitor metrics", risk: "medium" },
    { week: 6, daily: "50-60", total: "350-420", focus: "Approaching safe daily limit", risk: "medium" },
    { week: 7, daily: "60-70", total: "420-490", focus: "Near full warmup capacity", risk: "medium" },
    { week: 8, daily: "70-80", total: "490-560", focus: "Full warmup complete - maintain this level", risk: "low" },
  ];

  // Marketing emails can handle higher volumes (different sending pattern)
  const marketingSchedule = [
    { week: 1, daily: "100-200", total: "700-1400", focus: "Most engaged subscribers only", risk: "low" },
    { week: 2, daily: "200-400", total: "1400-2800", focus: "Add recent openers/clickers", risk: "low" },
    { week: 3, daily: "400-800", total: "2800-5600", focus: "Include 6-month engagers", risk: "medium" },
    { week: 4, daily: "800-1500", total: "5600-10500", focus: "Expand to 12-month engagers", risk: "medium" },
    { week: 5, daily: "1500-3000", total: "10500-21000", focus: "Add dormant but valid contacts", risk: "medium" },
    { week: 6, daily: "3000-5000", total: "21000-35000", focus: "Near full list coverage", risk: "high" },
    { week: 7, daily: "5000-8000", total: "35000-56000", focus: "Full list sends", risk: "high" },
    { week: 8, daily: "8000+", total: "56000+", focus: "Production volume reached", risk: "high" },
  ];

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case "low": return "bg-success/10 text-success border-success/20";
      case "medium": return "bg-warning/10 text-warning border-warning/20";
      case "high": return "bg-destructive/10 text-destructive border-destructive/20";
      default: return "bg-muted text-muted-foreground";
    }
  };

  return (
    <section className="py-8 sm:py-10">
      <div className="mb-6 sm:mb-8">
        <div className="flex items-center gap-2 mb-2">
          <Calendar className="w-5 h-5 text-primary" />
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground">
            8-Week Warmup Schedules
          </h2>
        </div>
        <p className="text-muted-foreground">
          Volume recommendations based on your use case. Adjust based on engagement metrics.
        </p>
      </div>

      {/* Cold Email Schedule */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-primary" />
            <h3 className="font-display text-lg font-semibold text-foreground">
              Cold Email / Sales Outreach
            </h3>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
              Per mailbox
            </span>
            <span className="text-xs text-success bg-success/10 px-2 py-0.5 rounded-full font-medium">
              Max 80/day recommended
            </span>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Week</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Daily Volume</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Weekly Total</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Focus</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Risk Level</th>
              </tr>
            </thead>
            <tbody>
              {coldEmailSchedule.map((row, index) => (
                <tr key={index} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                  <td className="py-3 px-4">
                    <span className="font-display font-semibold text-foreground">Week {row.week}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="font-mono text-sm text-foreground">{row.daily}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="font-mono text-sm text-muted-foreground">{row.total}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm text-muted-foreground">{row.focus}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`text-xs px-2 py-1 rounded-full border ${getRiskColor(row.risk)}`}>
                      {row.risk}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="text-xs text-muted-foreground/60 mt-2">
          Source: Instantly, Lemlist, Woodpecker warmup guides 2024-2025
        </div>
        <div className="mt-3 p-3 bg-info/5 border border-info/20 rounded-lg text-xs text-muted-foreground">
          <strong className="text-foreground">💡 Pro tip:</strong> For higher volumes, use multiple warmed mailboxes. 
          Example: 5 mailboxes × 80 emails/day = 400 emails/day total while maintaining deliverability.
        </div>
      </div>

      {/* Marketing Email Schedule */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-2 h-2 rounded-full bg-success" />
          <h3 className="font-display text-lg font-semibold text-foreground">
            Marketing / Newsletter Emails
          </h3>
          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
            Higher volume approach
          </span>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Week</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Daily Volume</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Weekly Total</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Focus</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Risk Level</th>
              </tr>
            </thead>
            <tbody>
              {marketingSchedule.map((row, index) => (
                <tr key={index} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                  <td className="py-3 px-4">
                    <span className="font-display font-semibold text-foreground">Week {row.week}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="font-mono text-sm text-foreground">{row.daily}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="font-mono text-sm text-muted-foreground">{row.total}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm text-muted-foreground">{row.focus}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`text-xs px-2 py-1 rounded-full border ${getRiskColor(row.risk)}`}>
                      {row.risk}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="text-xs text-muted-foreground/60 mt-2">
          Source: Mailgun, Validity, SendGrid warmup documentation 2024
        </div>
      </div>

      {/* Important Notes */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="bg-success/5 border border-success/20 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-success shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-foreground text-sm mb-1">Signs of Healthy Warmup</h4>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>• Open rates above 40%</li>
                <li>• Bounce rates below 2%</li>
                <li>• Spam complaints below 0.1%</li>
                <li>• Consistent inbox placement</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="bg-destructive/5 border border-destructive/20 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-foreground text-sm mb-1">Warning Signs to Slow Down</h4>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>• Open rates dropping below 20%</li>
                <li>• Bounce rates exceeding 5%</li>
                <li>• Spam complaints above 0.1%</li>
                <li>• Emails landing in spam folder</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
